// 引入必要的模組與檔案
import React, { useState, useEffect, useRef } from 'react';
import axios from './utils/axiosConfig';
import { Table, Button, Form, Modal, Card, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { useAuth } from './context/AuthContext'; // 引入 AuthContext 來取得用戶資訊
import { isSuperAdmin } from './utils/superAdmin'; // 引入 isSuperAdmin 檢查工具函數
import { hasPermission } from './utils/permissions'; // 引入權限檢查工具函數
import { useMediaQuery } from 'react-responsive'; // RWD 支援
import CompanyMap from './CompanyMap'; // 引入地圖組件

// Component 定義
const CompanyTable = () => {
  // 必須先調用所有 hooks，再進行條件判斷
  const { user } = useAuth();
  
  // 狀態宣告區域 - 用於管理前端資料及顯示控制
  const [companies, setCompanies] = useState([]); // 儲存公司資料
  const [categories, setCategories] = useState([]); // 儲存類別選項
  const [stores, setStores] = useState([]); // 儲存社別選項
  const [loading, setLoading] = useState(false); // 控制加載狀態
  const [filter, setFilter] = useState(''); // 搜尋過濾條件
  const [page, setPage] = useState(1); // 當前頁數
  const [hasMore, setHasMore] = useState(true); // 是否還有更多資料
  const [sortConfig, setSortConfig] = useState({ key: 's_id', direction: 'desc' }); // 排序設定
  const [showModal, setShowModal] = useState(false); // 控制 Modal 顯示
  const [selectedCompany, setSelectedCompany] = useState(null); // 當前選擇的公司
  const [successMessage, setSuccessMessage] = useState(''); // 成功訊息顯示
  const [viewMode, setViewMode] = useState('table'); // 顯示模式：'table', 'map'
  const token = localStorage.getItem('token'); // 取得 token 以供授權
  const [formData, setFormData] = useState({}); // 表單資料
  const observer = useRef(); // 用於無限滾動的觀察器
  const lastRef = useRef(); // 用於無限滾動的最後一個元素

  // 響應式判斷：768px 以下為手機版
  const isMobile = useMediaQuery({ maxWidth: 768 });


  const fetchCategories = async () => {
    try {
      // 更新 API 路徑為 /maincomp
      const response = await axios.get('/maincomp', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCategories(response.data); // 儲存類別選項
    } catch (error) {
      // Handle error silently
    }
  };

  const fetchStores = async () => {
    try {
      const response = await axios.get('/stores', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStores(response.data); // 儲存社別選項
    } catch (error) {
      // Handle error silently
    }
  };

  // 排序處理函數
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCompanies([]); // 重置資料
    setPage(1); // 重置為第一頁
    fetchCompanies(1, key, direction, filter); // 根據新排序規則重新加載資料
  };

  // 讀取公司資料函數
  const fetchCompanies = async (page, sortKey = sortConfig.key, sortDirection = sortConfig.direction, filter) => {
    setLoading(true);
    try {
      const response = await axios.get('/companies', {
        params: { filter, page, sortKey, sortDirection },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.length === 0 && page === 1) {
        setSuccessMessage('找不到符合條件的公司資料');
        setTimeout(() => setSuccessMessage(''), 3000);
      }

      setCompanies((prev) => (page === 1 ? response.data : [...prev, ...response.data]));
      setHasMore(response.data.length > 0);
      
    } catch (error) {
      // Handle error silently
    } finally {
      setLoading(false);
    }
  };

  // 刪除公司資料函數
  const handleDelete = async (id) => {
    if (window.confirm('確定要刪除這家公司嗎？')) {
      try {
        const response = await axios.delete(`/companies/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        setCompanies((prev) => prev.filter((company) => company.s_id !== id));
        setSuccessMessage(response.data.message);
        setTimeout(() => setSuccessMessage(''), 3000);
        
        fetchCompanies(1); // 刪除後重新載入第一頁資料
      } catch (error) {
        console.error('刪除公司錯誤:', error);
        if (error.response?.status === 403) {
          alert(`權限不足：${error.response.data.message || '您沒有刪除權限'}`);
        } else if (error.response?.status === 401) {
          alert('登入已過期，請重新登入');
        } else {
          alert('刪除公司時出現錯誤，請稍後重試');
        }
      }
    }
  };

  // 儲存或更新公司資料函數
  const handleSave = async () => {
    // 清理表單資料，將空字串轉為 null
    const cleanedFormData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [key, value === '' ? null : value])
    );

    console.log('準備儲存的資料:', cleanedFormData);

    try {
      let saveResponse;
      if (selectedCompany) { // 更新
        saveResponse = await axios.put(`/companies/${selectedCompany.s_id}`, cleanedFormData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setSuccessMessage(saveResponse.data.message);
      } else { // 新增
        saveResponse = await axios.post('/companies', cleanedFormData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setSuccessMessage(saveResponse.data.message);
      }

      // 🌟 自動獲取座標（如果有地址）
      const companyId = selectedCompany ? selectedCompany.s_id : saveResponse.data.id;
      if (cleanedFormData.s_address && cleanedFormData.s_address.trim() !== '') {
        try {
          console.log('正在獲取座標...');
          await axios.post(`/companies/${companyId}/geocode`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          console.log('座標已更新');
        } catch (geoError) {
          console.warn('獲取座標失敗:', geoError.message);
          // 不影響主流程，只記錄警告
        }
      }

      setTimeout(() => setSuccessMessage(''), 3000);
      fetchCompanies(1);
      setShowModal(false);
    } catch (error) {
      console.error('儲存公司錯誤:', error);
      console.error('錯誤詳情:', error.response?.data);

      let errorMessage = '儲存公司時出現錯誤，請稍後重試';

      if (error.response?.status === 403) {
        errorMessage = `權限不足：${error.response.data.message || '您沒有儲存權限'}`;
      } else if (error.response?.status === 401) {
        errorMessage = '登入已過期，請重新登入';
      } else if (error.response?.data?.message) {
        // 顯示後端返回的具體錯誤訊息
        errorMessage = error.response.data.message;
        if (error.response.data.detail) {
          errorMessage += `\n詳細資訊: ${error.response.data.detail}`;
        }
      } else if (error.message) {
        errorMessage = `發生錯誤: ${error.message}`;
      }

      alert(errorMessage);
    }
  };

  // 開啟編輯/新增公司資料的 Modal
  const openModal = (company = null) => {
    setSelectedCompany(company);
    // 新增模式時初始化所有欄位為空字串，避免 undefined 問題
    setFormData(company ? company : {
      s_group: '',
      s_com_name: '',
      s_business_item: '',
      s_boss: '',
      s_contact: '',
      s_tel: '',
      s_mobile: '',
      s_address: '',
      s_store_id: '',
      s_region: '',
      s_visit_features: '',
      s_visit_capacity: '',
      s_website: '',
      s_remark: ''
    });
    setShowModal(true);
  };

  // 副作用 (useEffect) - 資料讀取與滾動加載
  useEffect(() => {
    fetchCategories();
    fetchStores();
    fetchCompanies(page, sortConfig.key, sortConfig.direction, filter); // 根據頁數、排序和篩選條件加載公司資料
  }, [page, filter, sortConfig]);

  // 無限滾動邏輯 - 自動加載下一頁資料
  useEffect(() => {
    if (loading) return;
    if (observer.current) observer.current.disconnect(); // 清除先前觀察器
  
    // 設定新的 IntersectionObserver
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prevPage) => prevPage + 1); // 加載下一頁
      }
    });
  
    if (lastRef.current) {
      observer.current.observe(lastRef.current); // 監控最後一個參考
    }
  }, [loading, hasMore]);

  // 簡化權限：只有秘書能編輯
  const canEditRole = isSuperAdmin(user?.e_id) || user?.e_position === 17;
  const canViewRole = user?.e_group === 5 || canEditRole; // 社友(e_group=5)、管理員、秘書都可以查看

  // RWD 樣式
  const mobileStyles = `
    /* RWD: 手機版 Modal 表單樣式 */
    @media (max-width: 768px) {
      .modal .form-label {
        font-size: 16px !important;
        font-weight: bold;
        margin-bottom: 8px;
      }
      .modal .form-control,
      .modal .form-select {
        font-size: 16px !important;
        padding: 12px !important;
        min-height: 48px;
      }
      .modal .form-group,
      .modal .mb-3 {
        margin-bottom: 20px !important;
      }
      .modal-body {
        padding: 20px !important;
      }
      .modal-footer .btn {
        font-size: 16px !important;
        padding: 12px 24px !important;
        min-height: 48px;
      }
    }
  `;

  // UI 表現區域
  return (
    <div className="container-fluid mt-4" style={{ padding: isMobile ? '12px' : '20px' }}>
      <style>{mobileStyles}</style>
      {/* 權限檢查 - 所有社友都可以查看，只有管理員和秘書可以編輯 */}
      {!canViewRole ? (
        <div className="alert alert-warning">
          <h4>存取被拒絕</h4>
          <p>您沒有權限存取此頁面。只有社友、秘書或管理員才能查看工商參訪資料。</p>
        </div>
      ) : (
        <>
          {/* 顯示權限提示 */}
          {!canEditRole && (
            <div className="alert alert-info">
              <h5><i className="fas fa-info-circle me-2"></i>瀏覽模式</h5>
              <p className="mb-0">您目前以瀏覽模式查看工商參訪資料。如需新增、修改或刪除資料，請聯繫管理員或秘書。</p>
            </div>
          )}
          {/* 顯示成功訊息 */}
          {successMessage && (
            <div className="alert alert-success" role="alert">
              {successMessage}
            </div>
          )}
      {/* 搜尋框 */}
      <Row className="mb-3">
        <Col xs={12}>
          <Form.Control
            type="text"
            placeholder={isMobile ? "搜尋公司..." : "搜尋公司資料..."}
            value={filter}
            onChange={(e) => {
              setCompanies([]);
              setPage(1);
              setFilter(e.target.value);
            }}
            className={isMobile ? 'form-control-lg' : ''}
            style={{
              padding: isMobile ? '12px' : '10px',
              fontSize: isMobile ? '16px' : '14px'
            }}
          />
        </Col>
      </Row>

      {/* 按鈕群組 */}
      <Row className="mb-3 g-2">
        {/* 地圖切換按鈕 */}
        <Col xs={canEditRole ? 6 : 12} md={3}>
          <Button
            variant={viewMode === 'map' ? 'primary' : 'outline-primary'}
            className="w-100"
            size={isMobile ? 'lg' : 'sm'}
            onClick={() => setViewMode(viewMode === 'map' ? 'table' : 'map')}
            title={viewMode === 'map' ? '返回列表' : '地圖檢視'}
          >
            {viewMode === 'map' ? '📊 返回列表' : '🗺️ 地圖檢視'}
          </Button>
        </Col>
        {canEditRole && (
          <Col xs={6} md={3}>
            <Button
              variant="success"
              className="w-100"
              size={isMobile ? 'lg' : 'md'}
              onClick={() => openModal()}
            >
              ➕ 新增公司
            </Button>
          </Col>
        )}
      </Row>
      
      {/* 資料顯示區域 - 根據模式切換 */}
      {viewMode === 'map' ? (
        /* 地圖模式 */
        <CompanyMap />
      ) : (
        /* 表格/卡片模式 - 根據裝置自動切換 */
        <>
        {isMobile ? (
          /* 手機端 - 卡片式佈局 */
          <Row>
            {companies.map((company, index) => (
              <Col
                key={`${company.s_id}-${index}`}
                xs={12}
                className="mb-3"
                ref={index === companies.length - 1 ? lastRef : null}
              >
                <Card className="shadow-sm">
                  <Card.Header className="bg-primary text-white">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0 fw-bold">{company.s_com_name}</h6>
                      <Badge bg="light" text="dark">#{company.s_id}</Badge>
                    </div>
                  </Card.Header>
                  <Card.Body style={{ fontSize: '14px' }}>
                    <Row className="g-2 mb-2">
                      <Col xs={6}>
                        <strong>類別:</strong><br/>
                        <span className="text-muted">{company.sm_name}</span>
                      </Col>
                      <Col xs={6}>
                        <strong>社別:</strong><br/>
                        <span className="text-muted">{company.store_name || '-'}</span>
                      </Col>
                    </Row>
                    
                    {company.s_business_item && (
                      <div className="mb-2">
                        <strong>業務項目:</strong><br/>
                        <span className="text-muted">{company.s_business_item}</span>
                      </div>
                    )}
                    
                    {company.s_region && (
                      <div className="mb-2">
                        <strong>區域:</strong> <span className="text-muted">{company.s_region}</span>
                      </div>
                    )}
                    
                    <Row className="g-2 mb-2">
                      {company.s_boss && (
                        <Col xs={6}>
                          <strong>負責人:</strong><br/>
                          <span className="text-muted">{company.s_boss}</span>
                        </Col>
                      )}
                      {company.s_contact && (
                        <Col xs={6}>
                          <strong>聯絡人:</strong><br/>
                          <span className="text-muted">{company.s_contact}</span>
                        </Col>
                      )}
                    </Row>
                    
                    {company.s_tel && (
                      <div className="mb-2">
                        <strong>電話:</strong>{' '}
                        <a href={`tel:${company.s_tel}`} className="text-decoration-none">
                          {company.s_tel}
                        </a>
                      </div>
                    )}
                    
                    {company.s_mobile && (
                      <div className="mb-2">
                        <strong>手機:</strong>{' '}
                        <a href={`tel:${company.s_mobile}`} className="text-decoration-none">
                          {company.s_mobile}
                        </a>
                      </div>
                    )}
                    
                    {company.s_address && (
                      <div className="mb-2">
                        <strong>地址:</strong><br/>
                        <span className="text-muted">{company.s_address}</span>
                      </div>
                    )}
                    
                    {company.s_visit_capacity && (
                      <div className="mb-2">
                        <strong>參訪人數:</strong> <Badge bg="info">{company.s_visit_capacity} 人</Badge>
                      </div>
                    )}
                    
                    {company.s_website && (
                      <div className="mb-2">
                        <strong>網址:</strong>{' '}
                        <a href={company.s_website} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                          🔗 前往官網
                        </a>
                      </div>
                    )}
                    
                    {company.s_visit_features && (
                      <div className="mb-2">
                        <strong>參訪特色:</strong><br/>
                        <span className="text-muted small">{company.s_visit_features}</span>
                      </div>
                    )}
                    
                    {company.s_remark && (
                      <div className="mb-2">
                        <strong>備註:</strong><br/>
                        <span className="text-muted small">{company.s_remark}</span>
                      </div>
                    )}
                    
                    {canEditRole && (
                      <div className="d-grid gap-2 mt-3">
                        <Button
                          variant="outline-primary"
                          size="lg"
                          onClick={() => openModal(company)}
                        >
                          ✏️ 編輯
                        </Button>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
            {loading && (
              <Col xs={12} className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <div className="mt-2">載入中...</div>
              </Col>
            )}
          </Row>
        ) : (
          /* 桌面端 - 表格式佈局 */
        <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover variant="dark" className="table-striped table-dark" style={{ 
          tableLayout: 'auto',
          minWidth: '1400px'
        }}>
          <thead>
            <tr>
              <th onClick={() => handleSort('s_id')} style={{ width: '60px', cursor: 'pointer', fontSize: '14px' }}>編號</th>
              <th onClick={() => handleSort('sm_name')} style={{ width: '80px', cursor: 'pointer', fontSize: '14px' }}>類別</th>
              <th onClick={() => handleSort('store_name')} style={{ width: '80px', cursor: 'pointer', fontSize: '14px' }}>社別</th>
              <th onClick={() => handleSort('s_com_name')} style={{ minWidth: '150px', cursor: 'pointer', fontSize: '14px' }}>公司名稱</th>
              <th onClick={() => handleSort('s_business_item')} style={{ minWidth: '120px', cursor: 'pointer', fontSize: '14px' }}>業務項目</th>
              <th onClick={() => handleSort('s_region')} style={{ width: '100px', cursor: 'pointer', fontSize: '14px' }}>區域</th>
              <th onClick={() => handleSort('s_boss')} style={{ width: '80px', cursor: 'pointer', fontSize: '14px' }}>負責人</th>
              <th onClick={() => handleSort('s_contact')} style={{ width: '80px', cursor: 'pointer', fontSize: '14px' }}>聯絡人</th>
              <th onClick={() => handleSort('s_tel')} style={{ width: '100px', cursor: 'pointer', fontSize: '14px' }}>電話</th>
              <th onClick={() => handleSort('s_mobile')} style={{ width: '100px', cursor: 'pointer', fontSize: '14px' }}>手機</th>
              <th onClick={() => handleSort('s_address')} style={{ minWidth: '150px', cursor: 'pointer', fontSize: '14px' }}>地址</th>
              <th onClick={() => handleSort('s_visit_capacity')} style={{ width: '80px', cursor: 'pointer', fontSize: '14px' }}>參訪人數</th>
              <th onClick={() => handleSort('s_website')} style={{ minWidth: '120px', cursor: 'pointer', fontSize: '14px' }}>公司網址</th>
              <th style={{ minWidth: '150px', fontSize: '14px' }}>參訪特色及流程</th>
              <th onClick={() => handleSort('s_remark')} style={{ minWidth: '100px', cursor: 'pointer', fontSize: '14px' }}>備註</th>
              <th style={{ width: '80px', fontSize: '14px' }}>操作</th>
            </tr>
          </thead>
        <tbody>
        {companies.map((company, index) => (
          <tr key={`${company.s_id}-${index}`} ref={index === companies.length - 1 ? lastRef : null}>
            <td style={{ textAlign: 'center', fontSize: '14px', padding: '6px' }}>{company.s_id}</td>
            <td style={{ fontSize: '12px', padding: '6px' }}>{company.sm_name}</td>
            <td style={{ fontSize: '12px', padding: '6px' }}>{company.store_name || '-'}</td>
            <td style={{ 
              wordBreak: 'break-word', 
              fontSize: '13px',
              padding: '6px',
              fontWeight: '500'
            }}>
              {company.s_com_name}
            </td>
            <td style={{ 
              wordBreak: 'break-word', 
              fontSize: '12px',
              padding: '6px'
            }}>
              {company.s_business_item}
            </td>
            <td style={{ fontSize: '12px', padding: '6px' }}>{company.s_region || '-'}</td>
            <td style={{ fontSize: '12px', padding: '6px' }}>{company.s_boss}</td>
            <td style={{ fontSize: '12px', padding: '6px' }}>{company.s_contact}</td>
            <td style={{ fontSize: '12px', padding: '6px' }}>{company.s_tel}</td>
            <td style={{ fontSize: '12px', padding: '6px' }}>{company.s_mobile}</td>
            <td style={{ 
              wordBreak: 'break-word', 
              fontSize: '11px',
              padding: '6px',
              maxWidth: '150px'
            }}>
                {company.s_address}
            </td>
            <td style={{ textAlign: 'center', fontSize: '12px', padding: '6px' }}>
              {company.s_visit_capacity || '-'}
            </td>
            <td style={{ 
              wordBreak: 'break-all',
              fontSize: '11px',
              padding: '6px',
              maxWidth: '120px'
            }}>
              {company.s_website ? (
                <a href={company.s_website} target="_blank" rel="noopener noreferrer" 
                   style={{ color: '#17a2b8', textDecoration: 'none' }}>
                  {company.s_website}
                </a>
              ) : '-'}
            </td>
            <td style={{ 
              wordBreak: 'break-word', 
              fontSize: '11px',
              padding: '6px',
              maxWidth: '150px',
              lineHeight: '1.3'
            }}>
              {company.s_visit_features || '-'}
            </td>
            <td style={{ 
              wordBreak: 'break-word', 
              fontSize: '11px',
              padding: '6px',
              maxWidth: '100px'
            }}>
              {company.s_remark}
            </td>
            <td style={{ 
              textAlign: 'center', 
              padding: '6px',
              whiteSpace: 'nowrap'
            }}>
              {canEditRole ? (
                <>
                  <Button 
                    variant="info" 
                    size="sm" 
                    style={{ marginRight: '2px', fontSize: '11px', padding: '4px 8px' }}
                    onClick={() => openModal(company)}
                  >
                    編輯
                  </Button>
                  <Button 
                    variant="danger" 
                    size="sm" 
                    style={{ fontSize: '11px', padding: '4px 8px' }}
                    onClick={() => handleDelete(company.s_id)}
                  >
                    刪除
                  </Button>
                </>
              ) : (
                <span className="text-muted small">瀏覽模式</span>
              )}
              </td>
            </tr>
          ))}
          {loading && (
            <tr>
              <td colSpan="16" className="text-center">
                加載中...
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      </div>
        )}
      </>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        fullscreen={isMobile ? true : false}
        size={isMobile ? undefined : 'lg'}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: isMobile ? '18px' : '20px' }}>
            {selectedCompany ? '編輯公司' : '新增公司'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: isMobile ? '16px' : '14px' }}>
          <Form>
           {/* 類別下拉選單 - 使用 categories 來生成選項 */}
           <Form.Group className="mb-3">
              <Form.Label>類別</Form.Label>
              <Form.Control
                as="select"
                value={formData.s_group || ''}
                onChange={(e) => setFormData({ ...formData, s_group: e.target.value })}
              >
                <option value="">選擇類別</option>
                {categories.map((category) => (
                  <option key={category.sm_id} value={category.sm_id}>
                    {category.sm_name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>公司名稱</Form.Label>
              <Form.Control
                type="text"
                value={formData.s_com_name}
                onChange={(e) =>
                  setFormData({ ...formData, s_com_name: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>業務項目</Form.Label>
              <Form.Control
                type="text"
                value={formData.s_business_item}
                onChange={(e) =>
                  setFormData({ ...formData, s_business_item: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>公司負責人</Form.Label>
              <Form.Control
                type="text"
                value={formData.s_boss}
                onChange={(e) =>
                  setFormData({ ...formData, s_boss: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>聯絡人</Form.Label>
              <Form.Control
                type="text"
                value={formData.s_contact}
                onChange={(e) =>
                  setFormData({ ...formData, s_contact: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>電話</Form.Label>
              <Form.Control
                type="text"
                value={formData.s_tel}
                onChange={(e) =>
                  setFormData({ ...formData, s_tel: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>手機</Form.Label>
              <Form.Control
                type="text"
                value={formData.s_mobile}
                onChange={(e) =>
                  setFormData({ ...formData, s_mobile: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>地址</Form.Label>
              <Form.Control
                type="text"
                value={formData.s_address}
                onChange={(e) =>
                  setFormData({ ...formData, s_address: e.target.value })
                }
              />
            </Form.Group>
            {/* 社別下拉選單 */}
            <Form.Group className="mb-3">
              <Form.Label>社別</Form.Label>
              <Form.Control
                as="select"
                value={formData.s_store_id || ''}
                onChange={(e) => setFormData({ ...formData, s_store_id: e.target.value })}
              >
                <option value="">選擇社別</option>
                {stores.map((store) => (
                  <option key={store.ss_id} value={store.ss_id}>
                    {store.ss_name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            {/* 區域 */}
            <Form.Group className="mb-3">
              <Form.Label>區域</Form.Label>
              <Form.Control
                type="text"
                value={formData.s_region || ''}
                onChange={(e) =>
                  setFormData({ ...formData, s_region: e.target.value })
                }
              />
            </Form.Group>
            {/* 參訪特色及流程 */}
            <Form.Group className="mb-3">
              <Form.Label>參訪特色及流程</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.s_visit_features || ''}
                onChange={(e) =>
                  setFormData({ ...formData, s_visit_features: e.target.value })
                }
              />
            </Form.Group>
            {/* 參訪人數 */}
            <Form.Group className="mb-3">
              <Form.Label>參訪人數</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={formData.s_visit_capacity || ''}
                onChange={(e) =>
                  setFormData({ ...formData, s_visit_capacity: e.target.value })
                }
              />
            </Form.Group>
            {/* 公司網址 */}
            <Form.Group className="mb-3">
              <Form.Label>公司網址</Form.Label>
              <Form.Control
                type="url"
                value={formData.s_website || ''}
                onChange={(e) =>
                  setFormData({ ...formData, s_website: e.target.value })
                }
                placeholder="https://example.com"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>備註</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.s_remark || ''}
                onChange={(e) =>
                  setFormData({ ...formData, s_remark: e.target.value })
                }
              />
            </Form.Group>
            
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" className="me-2" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button variant="primary" size="sm" className="me-2"  onClick={handleSave}>
            儲存
          </Button>
        </Modal.Footer>
      </Modal>
        </>
      )}
    </div>
  );
};

export default CompanyTable;
